import { useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { ulid } from 'ulid';

import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import type {
	CreateTariffMutation,
	CreateTariffMutationVariables
} from '@/graphql/generated';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { TariffForm } from './tariff-form';
import { buildCreateTariffInput, type TariffFormValues } from './tariff-input';
import { TARIFF_CREATE_MUTATION } from './tariffs.gql.tsx';

export const TariffsCreate = () => {
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const go = useGo();

	const idempotentTariffId = useMemo(() => ulid(), []);

	useEffect(() => {
		setPageHeader({
			title: 'Create tariff',
			description: 'Define a new tariff configuration',
			showBackButton: true,
			onBackClick: () => go({ to: '/tariffs' })
		});

		return () => {
			resetPageHeader();
		};
	}, [go, resetPageHeader, setPageHeader]);

	const [createTariff, { loading: isSubmitting }] = useMutation<
		CreateTariffMutation,
		CreateTariffMutationVariables
	>(TARIFF_CREATE_MUTATION);

	const handleSubmit = async (formData: TariffFormValues) => {
		const payload = buildCreateTariffInput(formData, idempotentTariffId);

		try {
			const { data } = await createTariff(
				getMutationOptions('tariffs', { input: payload })
			);

			const message = extractCheckResultError(data?.createTariff);

			if (message) {
				showMutationErrorToast('tariffs', 'create', { message });
				return;
			}

			showMutationSuccessToast('tariffs', 'create');
			go({
				to: '/tariffs',
				state: { selectTariffId: idempotentTariffId }
			});
		} catch (error) {
			showMutationErrorToast('tariffs', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			<TariffForm
				mode="create"
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle="Create Tariff"
				onBack={() => go({ to: '/tariffs' })}
			/>
		</FormPageLayout>
	);
};
